/**
 * English UI catalogue for namespace 'checkerStyle' (W2-12a checker-runtime): titles (Settings > Checker), WHAT and
 * WHY texts and fix labels of the style rules ST-01 ... ST-09 (DESIGN.md §7.6). The rule file runs in the analysis
 * worker and cannot import this module, so it refers to the keys as strings ('checkerStyle.<id>');
 * style.rules.test.ts checks that every key it produces exists here and formats with the parameters given.
 * zh-CN lives in src/i18n/zh-CN/checkerStyle.ts (W3-09).
 */

import { catalogOf, defineMessages } from '../../../i18n/define';

const PROP =
  '{prop, select, alpha {opacity} moveX {position X} moveY {position Y} rotate {rotation} speed {scroll speed} scaleX {size X} scaleY {size Y} other {a property}}';

export const m = defineMessages('checkerStyle', {
  // ---- rule titles ----
  st01Title: 'Timing not confirmed',
  st02Title: 'Placeholder illustration',
  st03Title: 'Unused files',
  st04Title: 'Empty lines',
  st05Title: 'Long runs of straight segments',
  st06Title: 'Customised notes (RPE "Other" cautions)',
  st07Title: 'MP3 song',
  st08Title: 'FLAC song',
  st09Title: 'Incomplete chart details',

  // ---- ST-01 ----
  st01What: 'The tempo ({bpm} BPM) and the offset ({offset} ms) have not been confirmed yet.',
  st01Why:
    'Every note sits on the beat grid. If the tempo or the offset is a little off, all notes feel early or late in Phira, and fixing it later moves them all.',
  fixOpenTiming: 'Open Song & Timing',
  fixConfirmTiming: 'The timing is right',

  // ---- ST-02 ----
  st02What: 'The illustration is the generated placeholder.',
  st02Why:
    'Players see the illustration in the song list and blurred behind the chart; your own picture makes the chart recognisable.',
  fixChoosePicture: 'Choose a picture…',

  // ---- ST-03 ----
  st03What: '"{path}" is not used by the chart.',
  st03Why: 'Unused files make the package bigger to download; Phira never shows or plays them.',
  fixRemoveFile: 'Remove "{path}"',
  fixRemoveAllFiles: '{count, plural, one {Remove the unused file} other {Remove all # unused files}}',

  // ---- ST-04 ----
  st04What: '{line} has no notes and is never visible.',
  st04Why: 'Its opacity never rises above 0, so it draws nothing in Phira and only makes the line list longer.',
  st04WhatStill: '{line} has no notes and never moves.',
  st04WhyStill:
    'Phira draws it as the same plain line from start to end. If it is not meant as decoration, it only covers the screen and makes the line list longer.',
  fixDeleteLine: 'Delete {line}',
  fixDeleteAllLines: '{count, plural, one {Delete the empty line} other {Delete all # empty lines}}',
  fixShowLine: 'Show the line',

  // ---- ST-05 ----
  st05What: `{count} straight segments in a row on the ${PROP} of {line} ({from} to {to}).`,
  st05Why:
    'Long runs of short straight pieces usually come from baking. They make the chart bigger and hard to edit; a few eased segments usually look the same.',
  fixRemoveRedundant:
    '{count, plural, one {Remove the keyframe that does not change the motion} other {Remove the # keyframes that do not change the motion}}',
  fixSelectKeys: '{count, plural, one {Select the keyframe} other {Select the # keyframes}}',

  // ---- ST-06 ----
  st06WhatSize: '{count, plural, one {# note on {line} has} other {# notes on {line} have}} a custom width.',
  st06WhatFake:
    '{count, plural, one {# note on {line} is a Decoration (fake) note} other {# notes on {line} are Decoration (fake) notes}}.',
  st06WhatVisible: '{count, plural, one {# note on {line} has} other {# notes on {line} have}} a custom appear time.',
  st06WhySize: 'Players expect notes of the usual width; RPE lists custom widths as a caution.',
  st06WhyFake: 'Decoration (fake) notes are never judged; make sure players can tell them apart from real notes.',
  st06WhyVisible: 'Notes that appear late are hard to read; RPE lists custom appear times as a caution.',
  fixSelectNotes: '{count, plural, one {Select the note} other {Select the # notes}}',

  // ---- ST-07 / ST-08 ----
  st07What: 'The song "{path}" is an MP3 file.',
  st07Why:
    'MP3 decoders add a short delay that differs between players and browsers, so timing that looks right here can be a few milliseconds off in Phira. OGG or WAV keeps the timing exact.',
  st08What: 'The song "{path}" is a FLAC file.',
  st08Why: 'Phira plays FLAC, but RPE cannot open it; convert it if you or your co-charters edit the chart in RPE.',
  fixConvertToWav: 'Convert to WAV',
  fixChooseSong: 'Choose another file…',

  // ---- ST-09 ----
  st09What:
    '{field, select, name {The title} composer {The artist} charter {The charter} illustrator {The illustrator} level {The difficulty label} other {A detail}} is {state, select, empty {empty} other {still "UK" (unknown)}}.',
  st09Why: 'Players see these details in the song list, and Phira shows them when the chart is shared.',
  st09WhatDivision: 'The chart has no division tag (regular, troll, plain or visual).',
  st09WhyDivision: "Phira's upload form sorts every chart into one of these divisions.",
  fixEditDetail:
    '{field, select, name {Edit the title} composer {Edit the artist} charter {Edit the charter} illustrator {Edit the illustrator} level {Edit the difficulty label} other {Edit the details}}',
  fixEditTags: 'Choose a division',
  fixTagRegular: 'Tag it as "regular"',
});

export default catalogOf(m);
